import React, { useState } from "react";
import CcButton from "../../CoilConfigurator/components/CcButton";
import OpStatusPill from "../components/OpStatusPill";
import WarningBanner from "../components/WarningBanner";
import InputSpecificationSummary from "../components/InputSpecificationSummary";
import type { CadCheck, ConfigurationResult, EquipmentKind, EngineeringOverride } from "../models/types";

interface EquipmentDetailViewProps {
  result: ConfigurationResult;
  equipmentKind: EquipmentKind;
  onBack: () => void;
  onToggleCad: (checks: CadCheck[]) => void;
  onApplyOverride: (override: EngineeringOverride) => void;
}

const EquipmentDetailView = ({
  result,
  equipmentKind,
  onBack,
  onToggleCad,
  onApplyOverride,
}: EquipmentDetailViewProps) => {
  const equipment = equipmentKind === "blast-frame" ? result.blastFrame : result.inductionCoil;
  const [overrideValue, setOverrideValue] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [showCalcs, setShowCalcs] = useState(false);

  if (!equipment) {
    return (
      <section className="op-form-card">
        <p>Configuration cannot be automatically determined because required engineering data is unavailable.</p>
        <CcButton variant="secondary" onClick={onBack}>
          Back
        </CcButton>
      </section>
    );
  }

  const title = equipmentKind === "blast-frame" ? "Blast Frame" : "Induction Coil";
  const diameterOverride = equipment.overrides.find((o) => o.field === "Diameter Category");

  return (
    <div className="op-detail">
      <CcButton variant="link" onClick={onBack}>
        ← Back to results
      </CcButton>

      <header className="op-detail__summary">
        <div>
          <p className="op-kicker">{title}</p>
          <h2 className="op-section-title">{equipment.configurationId}</h2>
        </div>
        <OpStatusPill status={equipment.status} />
      </header>

      <InputSpecificationSummary
        project={result.project}
        pipe={result.pipe}
        process={result.process}
      />

      <dl className="op-summary-grid">
        <div>
          <dt>Diameter Category</dt>
          <dd>
            {equipment.diameterCategory}
            {diameterOverride ? (
              <span className="op-override-flag"> Override {diameterOverride.override}</span>
            ) : null}
          </dd>
        </div>
        {equipment.lengthCategory ? (
          <div>
            <dt>Length Category</dt>
            <dd>{equipment.lengthCategory}</dd>
          </div>
        ) : null}
        <div>
          <dt>Rule Version</dt>
          <dd>{equipment.ruleVersion}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{equipment.status}</dd>
        </div>
      </dl>

      {equipment.warnings.map((warning) => (
        <WarningBanner key={warning.title} warning={warning} />
      ))}

      <section className="op-form-card">
        <h3>Required Features</h3>
        <ul className="op-feature-list">
          {equipment.features
            .filter((f) => f.required)
            .map((feature) => (
              <li key={feature.name}>
                ✓ {feature.name}
                <span className="op-muted"> Required</span>
              </li>
            ))}
        </ul>
        <h3>Optional Features</h3>
        <ul className="op-feature-list">
          {equipment.features
            .filter((f) => !f.required && f.selected)
            .map((feature) => (
              <li key={feature.name}>○ {feature.name}</li>
            ))}
        </ul>
      </section>

      <section className="op-form-card">
        <h3>Equipment Options</h3>
        <table className="op-simple-table">
          <thead>
            <tr>
              <th>Option</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {equipment.options.map((option) => (
              <tr key={option.name}>
                <td>{option.name}</td>
                <td>{option.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="op-form-card">
        <h3>Engineering Calculations</h3>
        <dl className="op-summary-grid">
          {equipment.calculations.map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
        <CcButton variant="link" onClick={() => setShowCalcs((v) => !v)}>
          {showCalcs ? "Hide calculation details" : "Show calculation details"}
        </CcButton>
        {showCalcs
          ? equipment.decisions.map((decision) => (
              <div key={decision.question} className="op-decision">
                <h4>{decision.question}</h4>
                <p>
                  <strong>Input:</strong> {decision.input}
                </p>
                <p>
                  <strong>Rule:</strong> {decision.rule}
                </p>
                <p>
                  <strong>Result:</strong> {decision.result}
                </p>
                <p>
                  <strong>Rule Version:</strong> {decision.ruleVersion}
                </p>
              </div>
            ))
          : null}
      </section>

      {equipment.cadChecks.length > 0 ? (
        <section className="op-form-card">
          <h3>CAD Confirmation Required</h3>
          {equipment.cadChecks.map((check) => (
            <label key={check.id} className="op-check">
              <input
                type="checkbox"
                checked={check.confirmed}
                onChange={(event) =>
                  onToggleCad(
                    equipment.cadChecks.map((item) =>
                      item.id === check.id ? { ...item, confirmed: event.target.checked } : item,
                    ),
                  )
                }
              />
              {check.label}
            </label>
          ))}
          <p className="op-muted">Status: CAD CHECK REQUIRED</p>
        </section>
      ) : null}

      <section className="op-form-card">
        <h3>Engineering Override</h3>
        <p>
          Calculated Diameter Category: <strong>{equipment.diameterCategory}</strong>
        </p>
        <div className="op-form-grid">
          <div className="modal-field">
            <label htmlFor="overrideValue">Override</label>
            <input
              id="overrideValue"
              className="op-input"
              value={overrideValue}
              onChange={(e) => setOverrideValue(e.target.value)}
            />
          </div>
          <div className="modal-field">
            <label htmlFor="overrideReason">Reason</label>
            <input
              id="overrideReason"
              className="op-input"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
            />
          </div>
        </div>
        <CcButton
          variant="secondary"
          disabled={!overrideValue.trim() || !overrideReason.trim()}
          onClick={() => {
            onApplyOverride({
              field: "Diameter Category",
              calculated: equipment.diameterCategory,
              override: overrideValue.trim(),
              reason: overrideReason.trim(),
            });
            setOverrideValue("");
            setOverrideReason("");
          }}
        >
          Apply Override
        </CcButton>
        {equipment.overrides.map((item) => (
          <p key={`${item.field}-${item.override}`} className="op-override-note">
            {item.field}: calculated {item.calculated} → override {item.override} ({item.reason})
          </p>
        ))}
      </section>
    </div>
  );
};

export default EquipmentDetailView;
